import React, { createContext, useContext, useState, useCallback } from 'react';

const MusicPlayerContext = createContext({
  currentMusic: null,
  playlist: [],
  playing: false,
  setCurrentMusic: () => {},
  setPlaylist: () => {},
  addToPlaylist: () => {},
  removeFromPlaylist: () => {},
  playNext: () => {},
  playPrevious: () => {},
  clearPlaylist: () => {},
  showPlaylist: false,
  setShowPlaylist: () => {}
});

export const MusicPlayerProvider = ({ children }) => {
  const [currentMusic, setCurrentMusic] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);

  const addToPlaylist = useCallback((music) => {
    setPlaylist(prev => {
      // 避免重复添加
      if (prev.find(m => m.id === music.id)) {
        return prev;
      }
      return [...prev, music];
    });
  }, []);

  const removeFromPlaylistById = useCallback((musicId) => {
    setPlaylist(prev => {
      const newPlaylist = prev.filter(m => m.id !== musicId);
      // 如果删除的是当前播放的音乐，播放下一首
      if (currentMusic && currentMusic.id === musicId) {
        if (newPlaylist.length > 0) {
          const currentIndex = prev.findIndex(m => m.id === musicId);
          const nextIndex = currentIndex < newPlaylist.length ? currentIndex : newPlaylist.length - 1;
          setCurrentMusic(newPlaylist[nextIndex]);
        } else {
          setCurrentMusic(null);
          setPlaying(false);
        }
      }
      return newPlaylist;
    });
  }, [currentMusic]);

  const playNext = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    const nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    setCurrentMusic(playlist[nextIndex]);
  }, [playlist, currentMusic]);

  const playPrevious = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
    setCurrentMusic(playlist[prevIndex]);
  }, [playlist, currentMusic]);

  const clearPlaylist = useCallback(() => {
    setPlaylist([]);
    setCurrentMusic(null);
    setPlaying(false);
  }, []);

  const handlePlaylistChange = useCallback((music, index) => {
    setCurrentMusic(music);
    setPlaying(true);
  }, []);

  return (
    <MusicPlayerContext.Provider
      value={{
        currentMusic,
        playlist,
        playing,
        setCurrentMusic,
        setPlaylist,
        addToPlaylist,
        removeFromPlaylist: removeFromPlaylistById,
        playNext,
        playPrevious,
        clearPlaylist,
        handlePlaylistChange,
        setPlaying,
        showPlaylist,
        setShowPlaylist
      }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  }
  return context;
};

